
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "convex/react";
import { api } from "../../api";

interface TagsFilterProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  categoryFilter?: string; // Optional category filter to show only relevant tags
}

export const TagsFilter = ({ selectedTags, onTagsChange, categoryFilter }: TagsFilterProps) => {
  const { toast } = useToast();
  
  // Fetch categories from Convex API to get available tags
  const categoriesData = useQuery(api.appCategories.list.categories);
  
  // Get all unique tags from all categories, or filter by specific category
  const availableTags = categoriesData ? 
    (Array.isArray(categoriesData) ? categoriesData : [])
      .filter((category: any) => !categoryFilter || category.name === categoryFilter)
      .flatMap((category: any) => category.tags || [])
      .filter((tag: string, index: number, arr: string[]) => arr.indexOf(tag) === index) // Remove duplicates
    : [];

  const isLoading = categoriesData === undefined;

  const handleTagToggle = (tag: string, checked: boolean) => {
    if (checked) {
      onTagsChange([...selectedTags, tag]);
    } else {
      onTagsChange(selectedTags.filter(t => t !== tag));
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading tags...</div>;
  }

  if (availableTags.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        {categoryFilter ? `No tags found for ${categoryFilter} category` : "No tags available"}
      </div>
    );
  }

  return (
    <div className="space-y-2">
        <ScrollArea className="h-40">
          <div className="space-y-2">
            {availableTags.map((tag) => (
              <div key={tag} className="flex items-center space-x-2">
                <Checkbox
                  id={tag}
                  checked={selectedTags.includes(tag)}
                  onCheckedChange={(checked) => handleTagToggle(tag, !!checked)}
                />
                <label htmlFor={tag} className="text-sm cursor-pointer">
                  {tag}
                </label>
              </div>
            ))}
          </div>
        </ScrollArea>
      
      {selectedTags.length > 0 && (
        <div className="pt-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onTagsChange([])}
            className="text-xs"
          >
            Clear Selected ({selectedTags.length})
          </Button>
        </div>
      )}
    </div>
  );
};
